<template>
  <div class="py-8 space-y-8">
    <div class="lg:px-4 py-8 lg:py-16 flex flex-col items-center relative">
      <TContainer
        size="xl"
        class="w-full lg:px-16"
      >
        <AppHeaderLogo
          :to="localePath({ name: 'index' })"
          class="lg:hidden mb-4 self-center"
        />

        <div
          v-if="userCanManageCampgrounds"
          class="mb-4 flex justify-end"
        >
          <TButton
            :to="localePath({ name: ROUTE_BASE_NAME.rv_rentals_campground_create })"
            variant="secondary"
            size="sm"
           
          >
            {{ t('createCampground') }}
          </TButton>
        </div>

        <THeading
          :level="1"
          class="mb-2"
        >
          {{ t('headingTitle') }}
        </THeading>

        <p class="mb-4 font-semibold">
          {{ t('findPerfectSpot') }}
        </p>

        <div class="space-y-4">
          <TSubHeading>
            {{ t('heroIntro') }}
          </TSubHeading>

          <TSubHeading>
            {{ t('heroSupporting') }}
          </TSubHeading>
        </div>

        <div class="my-8 max-w-md mx-auto">
          <SearchFormMobileTrigger
            is-campgrounds
            class="lg:hidden"
          />
          <SearchFormBar
            is-campgrounds
            class="max-lg:hidden"
          />
        </div>
      </TContainer>
    </div>

    <TContainer v-if="data?.Data?.length">
      <THeading
        tag="h2"
        :level="3"
        class="mb-4"
      >
        {{ t('campgroundsnearyou') }}
      </THeading>

      <Grid
        :max-results="data?.Data?.length ?? pageSize"
        :sm-cols="2"
        :lg-cols="3"
        :md-rows="4"
        :lg-rows="3"
      >
        <template #default="{ getItemClass }">
          <CampgroundsCard
            v-for="(campground, index) in data?.Data"
            :key="campground?.Id ?? index"
            :index="index"
            :campground="campground"
            :is-loading="pending"
            :class="getItemClass(index)"
          />
        </template>
      </Grid>

      <div class="mt-8 flex justify-center">
        <TButton
          variant="secondary"
          :to="localePath({ name: ROUTE_BASE_NAME.campgrounds_search })"
        >
          {{ t('viewMoreCampgrounds') }}
        </TButton>
      </div>
    </TContainer>

    <TContainer
      v-if="nearbyRegions?.nearbyUS?.length || nearbyRegions?.nearbyCA?.length"
      class="my-24"
    >
      <GeneralNearbyLocations
        v-if="nearbyRegions?.nearbyUS?.length"
        :locations="nearbyRegions.nearbyUS"
        kind="Campgrounds"
        variant="state"
      />
      <GeneralNearbyLocations
        v-if="nearbyRegions?.nearbyCA?.length"
        :locations="nearbyRegions.nearbyCA"
        kind="Campgrounds"
        variant="province"
      />
    </TContainer>

    <TContainer
      size="xl"
      class="my-16"
    >
      <RvRentalsCampgroundDelivery />
      <div class="flex justify-center">
        <TButton
          variant="highlight"
          @click="navigateToRvSearch({ Delivery: true })"
        >
          {{ t('viewDeliverableRVs') }}
        </TButton>
      </div>
    </TContainer>
  </div>
</template>

<script setup lang="ts">
import { ROUTE_BASE_NAME } from '~/constants'
import type { Campground } from '~/types/rental-api-aliases'

definePageMeta({
  headerSearchFormVisibility: 'hidden',

  i18n: {
    paths: {
      en: '/campgrounds',
      fr: '/campings',
    },
  },
})

const { locale, t } = useI18n()
const { geolocation, isInCaOrUs } = useGeolocation()
const localePath = useLocalePath()
const { getNearbyRegions } = usePointOfInterest()
const { navigateToRvSearch } = useSearch()
const { userCanManageCampgrounds } = useUser()

const { data: nearbyRegions } = await useAsyncData(() => getNearbyRegions('campgrounds'))

useSeoHeadTags({
  title: computed(() => t('pageTitle')),
  description: computed(() => t('pageDescription')),
})

const pageSize = 9

const { data, pending } = await useRentals('/api/campgrounds', {
  query: {
    PageSize: pageSize,
    Language: lookupLocaleToLanguage(locale.value),
    SortBy: 'DistanceAsc',
    Latitude: isInCaOrUs.value ? geolocation.value?.latitude : undefined,
    Longitude: isInCaOrUs.value ? geolocation.value?.longitude : undefined,
  },
})

const { getDynamicImage, presets } = useImage()

function campgroundHeroImageUrl(campground: Campground): string | undefined {
  const path = campground.Photos?.[0]

  if (!path || !campground.Id) {
    return undefined
  }

  return getDynamicImage({
    path,
    preset: presets.LANDSCAPE_LG,
  }) ?? ''
}

const { defineCampground } = useAugmentedSchemaOrg()
const absoluteLocalePath = useAbsoluteLocalePath()

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      {
        text: t('pageLink.home'),
        to: localePath({ name: ROUTE_BASE_NAME.index }),
      },
      {
        text: t('campgrounds'),
        to: localePath({ name: ROUTE_BASE_NAME.campgrounds }),
      },
    ].map((breadcrumb) => ({
      name: breadcrumb.text,
      item: breadcrumb.to,
    })),
  }),

  defineWebPage({
    '@type': 'CollectionPage',
    'name': t('pageTitle'),
    'description': t('pageDescription'),
    'mainEntity': {
      '@type': 'ItemList',
      'numberOfItems': data.value?.Data?.length ?? 0,
      'itemListElement': data.value?.Data?.map((campground, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'item': defineCampground({
          id: `#campground-${index}`,
          name: campground.Name ?? '',
          url: absoluteLocalePath({ name: ROUTE_BASE_NAME.rv_rentals_campground, params: { campgroundSlug: campground.Id } }),
          image: campgroundHeroImageUrl(campground),
          latitude: campground.Latitude ?? undefined,
          longitude: campground.Longitude ?? undefined,
          city: campground.City ?? undefined,
          region: campground.Region ?? campground.RegionCode ?? undefined,
          countryCode: campground.CountryCode ?? undefined,
          numberOfRooms: campground.NumberOfSites ?? undefined,
        }),
      })) ?? [],
    },
  }),
])
</script>

<i18n lang="json">
  {
    "en": {
      "pageTitle": "RV campgrounds and RV parks in the United States and Canada",
      "pageDescription": "Search RV campgrounds and RV parks from coast to coast. Compare photos, amenities, full-hookup and pull-through sites, nightly rates, and locations to plan RV camping in the United States and Canada.",
      "campgrounds": "Campgrounds",
      "headingTitle": "Best campgrounds and RV parks in North America",
      "findPerfectSpot": "Find your perfect spot for RV camping",
      "heroIntro": "Browse RV parks and campgrounds across Canada and the United States. Compare amenities, photos, and locations to plan stops that work for your rig, your budget, and your route.",
      "heroSupporting": "Search by destination to discover full-hookup sites, pull-through spots, and family-friendly parks. Whether you are planning a weekend getaway or a cross-country road trip.",
      "campgroundsnearyou": "Campgrounds near you",
      "viewMoreCampgrounds": "View more campgrounds",
      "viewDeliverableRVs": "View deliverable RVs",
      "createCampground": "Create campground"
    },
    "fr": {
      "pageTitle": "Campings et parcs pour VR aux États-Unis et au Canada",
      "pageDescription": "Recherchez des campings et parcs pour VR partout en Amérique du Nord. Comparez photos, commodités, sites avec branchements complets et entrée directe, tarifs et emplacements pour planifier un voyage en VR au Canada et aux États-Unis.",
      "campgrounds": "Campings",
      "headingTitle": "Les meilleurs campings et parcs pour VR en Amérique du Nord",
      "findPerfectSpot": "Trouvez votre terrain idéal pour camping en VR",
      "heroIntro": "Parcourez des parcs pour VR et des terrains de camping au Canada et aux États-Unis. Comparez les commodités, les photos et les emplacements pour planifier des arrêts adaptés à votre véhicule, à votre budget et à votre itinéraire.",
      "heroSupporting": "Recherchez par destination pour découvrir des sites avec branchements complets, des emplacements en entrée directe et des parcs familiaux. Que ce soit pour une escapade de fin de semaine ou un voyage sur la route.",
      "campgroundsnearyou": "Terrains de camping près de vous",
      "viewMoreCampgrounds": "Voir plus de campings",
      "viewDeliverableRVs": "Voir les VRs livrables",
      "createCampground": "Créer un camping"
    }
  }
</i18n>
